import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import PaginationBar from "../components/PaginationBar";
import {
  fetchPurchaseRequests,
  selectPurchaseRequests,
  selectPurchaseRequestsError,
  selectPurchaseRequestsStatus,
} from "../features/purchaseRequests/purchaseRequestsSlice";
import "./StockTransfersPage.css";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function purchaseRequestStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "draft") return "Черновик";
  if (s === "in_progress") return "В процессе";
  if (s === "confirmed") return "Подтвержден";
  if (s === "sent") return "Отправлено";
  if (s === "received") return "Получено";
  if (s === "cancelled") return "Отменено";
  return status || "—";
}

function statusColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "draft") return "var(--c-text-sub)";
  if (s === "in_progress") return "var(--c-warning)";
  if (s === "confirmed") return "var(--c-accent)";
  if (s === "sent") return "var(--c-s3)";
  if (s === "received") return "var(--c-success)";
  if (s === "cancelled") return "var(--c-danger)";
  return "var(--c-text-sub)";
}

export default function PurchaseRequestsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const PAGE_SIZE = 25;
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [status, setStatus] = useState(() => searchParams.get("status") ?? "");
  const [createdFrom, setCreatedFrom] = useState(() => {
    const raw = searchParams.get("created_from");
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  });
  const [createdTo, setCreatedTo] = useState(() => {
    const raw = searchParams.get("created_to");
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(() => {
    const raw = searchParams.get("page");
    const n = Number(raw || 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  });

  const rows = useSelector(selectPurchaseRequests);
  const loadStatus = useSelector(selectPurchaseRequestsStatus);
  const error = useSelector(selectPurchaseRequestsError);

  const searchTrimmed = useMemo(() => search.trim(), [search]);
  const statusTrimmed = useMemo(() => status.trim(), [status]);
  const createdFromIso = useMemo(
    () => (createdFrom instanceof Date && !Number.isNaN(createdFrom.getTime()) ? createdFrom.toISOString() : null),
    [createdFrom]
  );
  const createdToIso = useMemo(
    () => (createdTo instanceof Date && !Number.isNaN(createdTo.getTime()) ? createdTo.toISOString() : null),
    [createdTo]
  );

  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTrimmed) next.set("search", searchTrimmed);
    if (statusTrimmed) next.set("status", statusTrimmed);
    if (createdFromIso) next.set("created_from", createdFromIso);
    if (createdToIso) next.set("created_to", createdToIso);
    if (page && page !== 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [createdFromIso, createdToIso, page, searchTrimmed, setSearchParams, statusTrimmed]);

  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlStatus = searchParams.get("status") ?? "";
    const urlFromRaw = searchParams.get("created_from");
    const urlToRaw = searchParams.get("created_to");
    const urlPageRaw = searchParams.get("page");
    const urlPageNum = Number(urlPageRaw || 1);
    const urlPage = Number.isFinite(urlPageNum) && urlPageNum > 0 ? Math.floor(urlPageNum) : 1;

    const urlFrom = (() => {
      if (!urlFromRaw) return null;
      const d = new Date(urlFromRaw);
      return Number.isNaN(d.getTime()) ? null : d;
    })();
    const urlTo = (() => {
      if (!urlToRaw) return null;
      const d = new Date(urlToRaw);
      return Number.isNaN(d.getTime()) ? null : d;
    })();

    if (urlSearch !== search) setSearch(urlSearch);
    if (urlStatus !== status) setStatus(urlStatus);
    if ((urlFromRaw || "") !== (createdFromIso || "")) setCreatedFrom(urlFrom);
    if ((urlToRaw || "") !== (createdToIso || "")) setCreatedTo(urlTo);
    if (urlPage !== page) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    dispatch(
      fetchPurchaseRequests({
        search: searchTrimmed,
        status: statusTrimmed,
        createdFrom: createdFromIso,
        createdTo: createdToIso,
      })
    );
  }, [createdFromIso, createdToIso, dispatch, refreshKey, searchTrimmed, statusTrimmed]);

  const totalCount = Array.isArray(rows) ? rows.length : 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (loadStatus === "succeeded" && page > totalPages) setPage(totalPages);
  }, [loadStatus, page, totalPages]);

  const visibleRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const start = (page - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  }, [PAGE_SIZE, page, rows]);

  return (
    <div className="stock-transfers-page">
      <div className="header">
        <div>
          <div className="title">Запросы на закуп</div>
          <div className="sub-title">
            Список заявок (данные из <span className="mono">/api/product/purchase-requests/</span>).
          </div>
        </div>

        <div className="header-actions">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск: id / name / склад / комментарий / товар…"
            className="search"
          />
          <Select
            value={status}
            onChange={(v) => { setStatus(v);
              setPage(1); }}
            options={[
              { value: "", label: "Все статусы" },
              { value: "draft", label: purchaseRequestStatusLabel("draft") },
              { value: "in_progress", label: purchaseRequestStatusLabel("in_progress") },
              { value: "confirmed", label: purchaseRequestStatusLabel("confirmed") },
              { value: "sent", label: purchaseRequestStatusLabel("sent") },
              { value: "received", label: purchaseRequestStatusLabel("received") },
              { value: "cancelled", label: purchaseRequestStatusLabel("cancelled") },
            ]}
            isSearchable={false}
          />
          <div className="date-group">
            <div className="date-label">Дата создания</div>
            <div className="date-row">
              <DatePicker
                selected={createdFrom}
                onChange={(d) => {
                  setCreatedFrom(d);
                  setPage(1);
                }}
                dateFormat="dd.MM.yyyy"
                placeholderText="c"
                isClearable
                customInput={<input className="date-input" />}
              />
              <DatePicker
                selected={createdTo}
                onChange={(d) => {
                  setCreatedTo(d);
                  setPage(1);
                }}
                dateFormat="dd.MM.yyyy"
                placeholderText="по"
                isClearable
                customInput={<input className="date-input" />}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() =>
              navigate("/warehouse/purchase-requests/new", {
                state: { from: `${location.pathname}${location.search}` },
              })
            }
            className="btn-primary"
          >
            + Добавить заявку
          </button>
          <button type="button" onClick={() => setRefreshKey((v) => v + 1)} className="btn">
            Обновить
          </button>
          {(createdFrom || createdTo) && (
            <button
              type="button"
              onClick={() => {
                setCreatedFrom(null);
                setCreatedTo(null);
                setPage(1);
              }}
              className="btn-ghost"
              title="Сбросить фильтр по дате"
            >
              Сброс даты
            </button>
          )}
        </div>
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div className="error">{error}</div>}

      <div className="body">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="th">ID</th>
                <th className="th">Название</th>
                <th className="th">Склад</th>
                <th className="th">Статус</th>
                <th className="th">Позиций</th>
                <th className="th">Сумма по себестоимости</th>
                <th className="th">В работе</th>
                <th className="th">Подтверждено</th>
                <th className="th">Отправлено</th>
                <th className="th">Получено</th>
                <th className="th">Создано</th>
                <th className="th">Обновлено</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && loadStatus === "succeeded" ? (
                <tr>
                  <td colSpan={12} className="td-muted">
                    Ничего не найдено
                  </td>
                </tr>
              ) : (
                visibleRows.map((r) => (
                  <tr
                    key={r.id}
                    className="tr"
                    onClick={() =>
                      navigate(`/warehouse/purchase-requests/${r.id}`, {
                        state: { from: `${location.pathname}${location.search}` },
                      })
                    }
                    title="Открыть заявку"
                  >
                    <td className="td-mono">{r.id}</td>
                    <td className="td">{r.name || "—"}</td>
                    <td className="td">{r.store_name || `Store #${r.store}` || "—"}</td>
                    <td className="td">
                      <span className="badge" style={{ borderColor: statusColor(r.status), color: statusColor(r.status) }}>
                        {purchaseRequestStatusLabel(r.status)}
                      </span>
                    </td>
                    <td className="td">{typeof r.items_count === "number" ? r.items_count : "—"}</td>
                    <td className="td">{formatMoney(r.total_cost)}</td>
                    <td className="td">{formatDateTime(r.in_progress_at)}</td>
                    <td className="td">{formatDateTime(r.confirmed_at)}</td>
                    <td className="td">{formatDateTime(r.sent_at)}</td>
                    <td className="td">{formatDateTime(r.received_at)}</td>
                    <td className="td">{formatDateTime(r.created_at)}</td>
                    <td className="td">{formatDateTime(r.updated_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <PaginationBar
            page={page}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            itemsOnPage={visibleRows.length}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}
